import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Button,
} from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import VerificationModal from "../../Modals/verificationModal";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  useDeleteUserMutation,
  useLazyGetUsersQuery,
  useUpdateUsersMutation,
} from "../../service/users";
import { showError, showToast } from "../../constants/toasts";
import { USER } from "../../types/User";
import moment from "moment";
import Pagination from "../../components/Panigation";
import ConfirmationDialog from "../../Modals/confirmationModal";
import usePermission from "../../hooks/usePermission";

const UserList = ({
  search,
  page,
  setPage,
}: {
  search?: string;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
}) => {
  const navigate = useNavigate();
  const { isAdd, isEdit, isDelete } = usePermission({
    label: "Manage Users",
    page_permission: "isView",
  });
  const [openModal, setOpenModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<USER | null>(null);
  const [rows, setRows] = useState<USER[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit] = useState<number>(10);

  const [getUserList, { isLoading }] = useLazyGetUsersQuery();
  const [updateUserStatus, { isLoading: isUpdatingStatus }] =
    useUpdateUsersMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedUser(null);
  };

  const handleStatusChange = async (user: USER) => {
    try {
      const response = await updateUserStatus({
        id: user._id,
        body: { isBlocked: !user.isBlocked },
      }).unwrap();

      if (response.code === 200) {
        showToast(
          `User ${!user.isBlocked ? "blocked" : "activated"} successfully`
        );
        // Update local state
        setRows((prevRows) =>
          prevRows.map((u) =>
            u._id === user._id ? { ...u, isBlocked: !user.isBlocked } : u
          )
        );
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to update user status");
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const response = await deleteUser({ id: selectedUser._id }).unwrap();

      if (response.code === 200) {
        showToast("User deleted successfully");
        handleCloseDeleteModal();

        // Refresh the list or remove from local state
        if (rows.length === 1 && page > 1) {
          // If it's the last item on the page, go to previous page
          setPage((prev) => prev - 1);
          getUsers(page - 1);
        } else {
          getUsers(page);
        }
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to delete user");
    }
  };

  const getUsers = async (p?: number) => {
    try {
      const response = await getUserList({
        page: p || page,
        limit,
        ...(search && { search }),
      }).unwrap();
      if (response.code === 200) {
        setRows(response?.data?.list || []);
        setTotalCount(response?.data?.totalCount || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setRows([]);
      setTotalCount(0);
    }
  };

  // Open delete confirmation modal
  const handleOpenDeleteModal = (user: USER) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    getUsers(newPage);
  };

  useEffect(() => {
    getUsers();
  }, [search]);

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Date Registered</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.length > 0 ? (
              rows?.map((user) => (
                <TableRow hover key={user?._id}>
                  <TableCell>
                    <Box
                      component="a"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/manage-users/detail/${user?._id}`, {
                          state: { user },
                        });
                      }}
                      className="user_block click_action"
                    >
                      <p>
                        <b>
                          {user?.firstName || ""} {user?.lastName || ""}
                        </b>
                        <span>
                          {user.email}
                          {/* <img src="/static/images/tick_icon.svg" alt="verified" /> */}
                        </span>
                        <span>
                          {user?.countryCode || ""}
                          {user?.phone || ""}
                        </span>
                      </p>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user?.createdAt
                      ? moment(user.createdAt).format(" lll ")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <SwitchToggle
                      disabled={!isEdit}
                      onChange={() => {
                        handleStatusChange(user);
                      }}
                      checked={!user?.isBlocked}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="table_action_wrapper">
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() =>
                            navigate(`/manage-users/detail/${user?._id}`)
                          }
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                        {isEdit && (
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-users/edit/${user?._id}`)
                            }
                          >
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Edit"
                            />
                          </IconButton>
                        )}
                        {isDelete && (
                          <IconButton
                            onClick={() => {
                              handleOpenDeleteModal(user);
                            }}
                          >
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow hover>
                <TableCell colSpan={4}>
                  <Box textAlign={"center"}>No data found</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {totalCount > 0 && (
          // <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            module={rows}
            page={page}
            onPageChange={handlePageChange}
            totalPages={totalPages}
            setPage={setPage}
            totalCount={totalCount}
          />
          // </Box>
        )}
      </TableContainer>
      <VerificationModal
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
      />
      <ConfirmationDialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteUser}
        title="Delete User"
        confirmText="Delete"
        cancelText="Cancel"
        message={`Are you sure you want to delete user "${
          selectedUser?.firstName || ""
        } ${selectedUser?.lastName || ""}"?`}
        isLoading={isDeleting}
      />
    </>
  );
};

export default UserList;
